import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { StoryMedia } from '@kick/types';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useAnuncios, type Anuncio } from '@/lib/anuncios';

/**
 * Carrusel de anuncios de la portada.
 *
 * Un banner ancho y no una fila de círculos: el anuncio de un comercio es una
 * carta, un combo o una promoción, y eso se lee. Un círculo de sesenta píxeles
 * obliga a tocar para enterarse de qué se trata, y la mayoría no toca.
 *
 * Avanza solo, cada anuncio los segundos que le pusieron al publicarlo, y
 * respeta a quien pidió menos movimiento en su sistema.
 */
export function CarruselAnuncios() {
  const anuncios = useAnuncios();
  const [indice, setIndice] = useState(0);
  /** Pausa momentánea: el dedo arrastrando, o alguien navegando con teclado. */
  const [pausado, setPausado] = useState(false);
  /** Pausa pedida a propósito con el botón, y que solo el botón levanta. */
  const [pausaManual, setPausaManual] = useState(false);
  const detenido = pausado || pausaManual;
  const contenedor = useRef<HTMLDivElement>(null);
  const vistos = useRef(new Set<string>());

  /**
   * El arreglo vacío se memoriza.
   *
   * Sin esto, cada render crea uno nuevo y los efectos que dependen de la
   * lista se vuelven a ejecutar siempre: el temporizador del carrusel se
   * reiniciaría en bucle y las diapositivas no llegarían a cambiar nunca.
   */
  const lista = useMemo(() => anuncios.data ?? [], [anuncios.data]);
  const total = lista.length;

  const ir = useCallback(
    (siguiente: number) => {
      if (total === 0) return;
      // Da la vuelta en los dos sentidos: nunca hay un extremo muerto.
      setIndice(((siguiente % total) + total) % total);
    },
    [total],
  );

  /**
   * Menos movimiento no significa carrusel congelado.
   *
   * `prefers-reduced-motion` se activa solo en Windows con los efectos de
   * animación apagados, que es una opción de rendimiento que mucha gente
   * cambia sin pensar en accesibilidad. Atarle el avance automático dejaba el
   * banner clavado en el primer anuncio para media máquina, y desde fuera
   * parecía —era— que el carrusel estaba roto.
   *
   * Lo que molesta a quien pide menos movimiento es el movimiento en sí: el
   * acercamiento lento de la imagen y el fundido largo. Eso es lo que se
   * apaga. Los anuncios siguen turnándose, ahora con un corte seco, y quien
   * quiera pararlos tiene el botón de pausa.
   */
  const sinMovimiento =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * El temporizador depende solo de números, nunca de la lista.
   *
   * Esta es la diferencia entre que avance y que no. Si el efecto dependiera
   * del arreglo, cada vez que la consulta se rehace —al volver a la pestaña,
   * al refrescar por reloj— llegaría un arreglo nuevo aunque el contenido sea
   * idéntico, el efecto se volvería a montar y el `clearTimeout` de su
   * limpieza mataría la cuenta atrás justo antes de que saltara. El carrusel
   * se queda quieto para siempre sin que nada parezca roto.
   *
   * Con `indice` y `segundos` —dos números— eso no puede pasar.
   */
  const segundos = lista[indice]?.durationSeconds ?? 6;

  useEffect(() => {
    if (detenido || total <= 1) return;

    const reloj = setTimeout(() => setIndice((i) => (i + 1) % total), segundos * 1000);
    return () => clearTimeout(reloj);
  }, [indice, segundos, detenido, total]);

  /**
   * Si la lista encoge, el índice tiene que caber en ella.
   *
   * Pasa de verdad: un anuncio vence o lo retiran desde el panel mientras
   * alguien tiene la portada abierta. Sin esto el índice apunta al vacío, no
   * hay diapositiva activa y el banner se queda en negro.
   */
  useEffect(() => {
    if (total > 0 && indice >= total) setIndice(0);
  }, [indice, total]);

  /**
   * Cada anuncio que llega a verse cuenta una vez.
   *
   * El carrusel vuelve a pasar por el mismo cada vuelta, y contar cada pasada
   * inflaría las cifras hasta volverlas inútiles: lo que se mide es cuántas
   * personas lo vieron, no cuántas veces giró la rueda.
   */
  const idActual = lista[indice]?.id;

  useEffect(() => {
    if (!idActual || vistos.current.has(idActual)) return;

    vistos.current.add(idActual);
    void api.post(`/anuncios/${idActual}/visto`).catch(() => {});
  }, [idActual]);

  /**
   * Deslizar con el dedo, que en el celular es el gesto natural.
   *
   * Va con eventos de puntero y no de toque: los mismos manejadores sirven
   * para el dedo, el ratón y un lápiz, sin escribir dos veces lo mismo.
   *
   * `arrastrado` existe porque cada diapositiva es un enlace: sin esa marca,
   * soltar el dedo tras el deslizamiento contaría como un toque y se iría al
   * comercio en vez de pasar al siguiente anuncio.
   */
  const inicioX = useRef<number | null>(null);
  const arrastrado = useRef(false);

  const alTocar = (e: React.PointerEvent): void => {
    inicioX.current = e.clientX;
    arrastrado.current = false;
    setPausado(true);
  };

  const alSoltar = (e: React.PointerEvent): void => {
    const desde = inicioX.current;
    inicioX.current = null;
    setPausado(false);
    if (desde === null) return;

    const recorrido = e.clientX - desde;

    // 45 px: por debajo es el temblor normal de un toque, no una intención.
    if (Math.abs(recorrido) < 45) return;

    arrastrado.current = true;
    ir(indice + (recorrido < 0 ? 1 : -1));
  };

  if (total === 0) return null;

  return (
    <section
      ref={contenedor}
      aria-label="Anuncios de comercios"
      aria-roledescription="carrusel"
      className="relative mb-6 touch-pan-y select-none overflow-hidden rounded-kick bg-kick-dark shadow-card"
      /**
       * No se pausa al pasar el ratón por encima.
       *
       * Parece buena idea y no lo es: el banner ocupa el ancho de la portada y
       * está arriba del todo, así que el puntero se queda sobre él sin que
       * nadie lo pretenda —tras hacer clic, tras desplazarse—. El carrusel se
       * quedaba congelado y daba la impresión de estar roto.
       *
       * Sí se detiene cuando hay una intención real: alguien navegando con el
       * teclado, o un dedo arrastrando.
       */
      onFocusCapture={() => setPausado(true)}
      onBlurCapture={() => setPausado(false)}
      onPointerDown={alTocar}
      onPointerUp={alSoltar}
      onPointerCancel={() => {
        inicioX.current = null;
        setPausado(false);
      }}
      onClickCapture={(e) => {
        // Se deslizó: el enlace no debe dispararse al levantar el dedo.
        if (arrastrado.current) {
          e.preventDefault();
          e.stopPropagation();
          arrastrado.current = false;
        }
      }}
    >
      {/*
        Relación fija, más baja en escritorio.
        Sin una altura estable, cada anuncio de proporción distinta empujaría
        el resto de la portada hacia abajo al cambiar de diapositiva.
      */}
      <div className="relative aspect-[16/10] sm:aspect-[16/6]">
        {lista.map((anuncio, i) => (
          <Diapositiva
            key={anuncio.id}
            anuncio={anuncio}
            activa={i === indice}
            pausada={detenido}
            sinMovimiento={sinMovimiento}
          />
        ))}
      </div>

      {total > 1 && (
        <>
          <Flecha lado="izquierda" onClick={() => ir(indice - 1)} />
          <Flecha lado="derecha" onClick={() => ir(indice + 1)} />

          {/*
            Parar el carrusel tiene que ser posible sin adivinar.
            Es contenido que se mueve solo, y quien necesite leerlo con calma
            —o a quien le moleste el movimiento— debe poder detenerlo con un
            control a la vista, no con una preferencia escondida del sistema.
          */}
          <button
            onClick={() => setPausaManual((p) => !p)}
            aria-label={pausaManual ? 'Reanudar los anuncios' : 'Pausar los anuncios'}
            className="absolute bottom-2.5 right-3 z-20 grid h-7 w-7 place-items-center rounded-full bg-black/35 text-white backdrop-blur transition hover:bg-black/60"
          >
            {pausaManual ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
          </button>

          <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 gap-1.5">
            {lista.map((a, i) => (
              <button
                key={a.id}
                onClick={() => ir(i)}
                aria-label={`Anuncio ${i + 1} de ${total}`}
                aria-current={i === indice}
                className={cn(
                  'h-1.5 rounded-full transition-all duration-300',
                  i === indice ? 'w-6 bg-white' : 'w-1.5 bg-white/50 hover:bg-white/80',
                )}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function Diapositiva({
  anuncio,
  activa,
  pausada,
  sinMovimiento,
}: {
  anuncio: Anuncio;
  activa: boolean;
  pausada: boolean;
  sinMovimiento: boolean;
}) {
  const video = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const v = video.current;
    if (!v) return;

    if (activa && !pausada) void v.play().catch(() => {});
    else v.pause();
  }, [activa, pausada]);

  /**
   * Con comercio, la diapositiva entera es un enlace a su ficha. Sin él, es
   * solo la pieza: un anuncio general de la plataforma al que tocarlo no le
   * pasa nada, y que por eso tampoco promete «Descubrir».
   */
  const clase = cn(
    'absolute inset-0',
    // Con menos movimiento, corte seco en vez de fundido largo: el anuncio
    // cambia igual, pero sin nada deslizándose por la pantalla.
    sinMovimiento ? 'transition-none' : 'transition-opacity duration-700 ease-out',
    activa ? 'opacity-100' : 'pointer-events-none opacity-0',
  );

  // El contenido se arma una vez y se mete en el marco que toque. No se
  // define un componente aquí adentro: React lo tomaría por uno nuevo en
  // cada render y desmontaría la pieza —video incluido— a cada tic.
  const contenido = (
    <>
      {/*
        Fondo desenfocado con la misma imagen.
        Un anuncio vertical o cuadrado dejaría franjas negras a los lados; así
        el hueco se rellena con su propio color y la pieza se ve entera, sin
        recortar el texto que casi siempre trae.
      */}
      {anuncio.mediaType !== StoryMedia.VIDEO && (
        <img
          src={anuncio.mediaUrl}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl saturate-150"
        />
      )}

      {anuncio.mediaType === StoryMedia.VIDEO ? (
        <video
          ref={video}
          src={anuncio.mediaUrl}
          className="relative h-full w-full object-cover"
          muted
          loop
          playsInline
          preload="metadata"
        />
      ) : (
        <img
          src={anuncio.mediaUrl}
          alt={anuncio.caption ?? (anuncio.merchantName ? `Anuncio de ${anuncio.merchantName}` : 'Anuncio')}
          className={cn(
            'relative h-full w-full object-contain',
            // Acercamiento lentísimo mientras está en pantalla: da vida sin
            // distraer. Se apaga con la preferencia de menos movimiento.
            activa && !sinMovimiento && 'animate-[acercar_12s_ease-out_forwards]',
          )}
        />
      )}

      {/* Velo inferior: sin él, el texto blanco desaparece sobre una foto clara. */}
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />

      <div className="absolute inset-x-0 bottom-0 flex flex-wrap items-end justify-between gap-3 p-4 pb-10 sm:p-6 sm:pb-12">
        <div className="flex min-w-0 items-center gap-3">
          {anuncio.merchantLogo && (
            <img
              src={anuncio.merchantLogo}
              alt=""
              className="h-11 w-11 shrink-0 rounded-full border-2 border-white/80 object-cover"
            />
          )}
          <div className="min-w-0">
            {anuncio.merchantName && (
              <p className="truncate text-lg font-bold text-white drop-shadow sm:text-xl">
                {anuncio.merchantName}
              </p>
            )}
            {anuncio.caption && (
              <p
                className={cn(
                  'drop-shadow',
                  anuncio.merchantName
                    ? 'line-clamp-1 text-sm text-white/85'
                    : 'line-clamp-2 text-lg font-bold text-white sm:text-xl',
                )}
              >
                {anuncio.caption}
              </p>
            )}
          </div>
        </div>

        {anuncio.merchantSlug && (
          <span className="flex shrink-0 items-center gap-2 rounded-full bg-kick-red px-5 py-2.5 font-bold text-white shadow-lg transition group-hover:gap-3">
            Descubrir
            <ArrowRight className="h-4 w-4" />
          </span>
        )}
      </div>
    </>
  );

  return anuncio.merchantSlug ? (
    <Link
      to={`/c/${anuncio.merchantSlug}`}
      tabIndex={activa ? 0 : -1}
      aria-hidden={!activa}
      className={clase}
    >
      {contenido}
    </Link>
  ) : (
    <div aria-hidden={!activa} className={clase}>
      {contenido}
    </div>
  );
}

function Flecha({ lado, onClick }: { lado: 'izquierda' | 'derecha'; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-label={lado === 'izquierda' ? 'Anuncio anterior' : 'Anuncio siguiente'}
      className={cn(
        'absolute top-1/2 z-20 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full',
        'bg-black/35 text-white backdrop-blur transition hover:bg-black/60',
        lado === 'izquierda' ? 'left-3' : 'right-3',
      )}
    >
      {lado === 'izquierda' ? (
        <ChevronLeft className="h-5 w-5" />
      ) : (
        <ChevronRight className="h-5 w-5" />
      )}
    </button>
  );
}
